
import { Company, Project, Agent, AgentResult, AppSettings, AIProvider, CostMode, GeminiModel } from '../types';
import { GeminiService } from './geminiService';
import { normalizeSettings, SettingsService } from './settingsService';
import { MemoryService } from './memoryService';
import { OpenRouterService } from './openRouterService';
import { LocalLLMService } from './localLLMService';
import { ContactEnrichmentService } from './contactEnrichmentService';
import { LeadSearchService } from './leadSearchService';
import { Type } from "@google/genai";

const getSettings = () => SettingsService.getSettings();

const getSearchRegionInstruction = (settings: AppSettings) => {
  switch (settings.searchRegion) {
    case 'benelux':
      return {
        label: 'Benelux',
        prompt: 'Zoekgebied: Benelux. Zoek alleen bedrijven in Nederland, België of Luxemburg. Gebruik Nederlandse, Belgische en Luxemburgse contactbronnen.',
        queryContext: 'Benelux Nederland België Luxemburg',
      };
    case 'europe':
      return {
        label: 'Europa',
        prompt: 'Zoekgebied: Europa. Zoek Europese bedrijven en geef land/stad expliciet mee. Contacteerbaarheid blijft belangrijker dan geografische spreiding.',
        queryContext: 'Europa European company contact phone email address',
      };
    case 'nl':
    default:
      return {
        label: 'Nederland',
        prompt: 'Zoekgebied: Nederland. Zoek Nederlandse bedrijven en Nederlandse contactbronnen zoals officiële sites, telefoonboekachtige bronnen en kaart/adresdata.',
        queryContext: 'Nederland Nederlands bedrijf contact telefoon e-mail adres',
      };
  }
};

const getSelectedSkillsContext = (project: Project, options?: any) => {
  const selectedSkills = options?.selectedSkills || project.selectedSkills || [];
  if (!Array.isArray(selectedSkills) || selectedSkills.length === 0) return '';

  const pick = (...values: any[]) => {
    for (const value of values) {
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }
    return '';
  };

  return `\nACTIEVE EXPERTISE SKILLS:\n${selectedSkills
    .map((skill: any) => `- ${pick(skill.name, skill.id)} (${pick(skill.category, 'general')}): ${pick(skill.description, skill.path, 'Gebruik deze expertise actief in je analyse en formulering.')}`)
    .join('\n')}`;
};

const asObject = (value: any): Record<string, any> =>
  value && typeof value === 'object' && !Array.isArray(value) ? value : {};

const unwrapObjectPayload = (value: any): Record<string, any> => {
  const raw = asObject(value);
  const nestedCandidate = [
    raw.data,
    raw.result,
    raw.output,
    raw.payload,
    raw.research,
    raw.analysis,
    raw.analyse,
  ].find((item) => item && typeof item === 'object' && !Array.isArray(item));

  if (nestedCandidate) {
    return asObject(nestedCandidate);
  }

  const entries = Object.entries(raw);
  if (entries.length === 1) {
    const [, nestedValue] = entries[0];
    if (nestedValue && typeof nestedValue === 'object' && !Array.isArray(nestedValue)) {
      return asObject(nestedValue);
    }
  }

  return raw;
};

const firstString = (...values: any[]): string => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
};

const asStringArray = (value: any): string[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      if (typeof item === 'string') return item.trim();
      if (item && typeof item === 'object') {
        return firstString(
          item.text,
          item.summary,
          item.title,
          item.name && item.doel ? `${item.name}: ${item.doel}` : '',
          item.naam && item.doel ? `${item.naam}: ${item.doel}` : '',
          item.name && item.strength ? `${item.name}: ${item.strength}` : '',
        );
      }
      return '';
    })
    .filter(Boolean);
};

const uniqueStringList = (...values: any[]): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];

  const append = (value: any) => {
    if (!value) return;

    if (Array.isArray(value)) {
      value.forEach(append);
      return;
    }

    const candidates =
      typeof value === 'string'
        ? value
            .split(/\n+/)
            .map((item) => item.trim())
            .filter(Boolean)
        : asStringArray([value]);

    candidates.forEach((candidate) => {
      const normalized = candidate.replace(/\s+/g, ' ').trim();
      const key = normalized.toLowerCase();
      if (!normalized || seen.has(key)) return;
      seen.add(key);
      result.push(normalized);
    });
  };

  values.forEach(append);
  return result;
};

const normalizeGroundingSources = (groundingChunks?: any[]): Array<{ title: string; uri: string }> => {
  if (!Array.isArray(groundingChunks)) return [];

  return groundingChunks
    .map((chunk: any) => {
      const web = chunk?.web || chunk;
      const title = firstString(web?.title, web?.site_name, web?.hostname);
      const uri = firstString(web?.uri, web?.url, web?.href);
      if (!title || !uri) return null;
      return { title, uri };
    })
    .filter((item): item is { title: string; uri: string } => Boolean(item));
};

const mergeSourceLists = (...lists: any[]): Array<{ title: string; uri: string }> => {
  const seen = new Set<string>();
  const merged: Array<{ title: string; uri: string }> = [];

  lists.flat().forEach((item: any) => {
    if (!item) return;
    const title = firstString(item.title, item.name);
    const uri = firstString(item.uri, item.url, item.href);
    if (!title || !uri) return;

    const key = `${title.toLowerCase()}|${uri.toLowerCase()}`;
    if (seen.has(key)) return;
    seen.add(key);
    merged.push({ title, uri });
  });

  return merged;
};

const normalizeEvidenceItems = (value: any): Array<{ claim: string; source?: string; confidence?: string }> => {
  if (!Array.isArray(value)) return [];

  const items: Array<{ claim: string; source?: string; confidence?: string } | null> = value
    .map((item) => {
      if (typeof item === 'string') {
        const claim = item.trim();
        return claim ? { claim } : null;
      }

      if (!item || typeof item !== 'object') return null;

      const claim = firstString(
        item.claim,
        item.bewering,
        item.insight,
        item.inzicht,
        item.text,
        item.summary,
      );

      if (!claim) return null;

      return {
        claim,
        source: firstString(item.source, item.bron, item.title, item.url, item.uri),
        confidence: firstString(
          item.confidence,
          item.betrouwbaarheid,
          typeof item.score === 'number' ? `${Math.round(item.score * 100)}%` : '',
        ),
      };
    });

  return items.filter((item): item is { claim: string; source?: string; confidence?: string } => Boolean(item));
};

const selectGeminiResearchModel = (modelId?: string): string => {
  const strippedModel = String(modelId || '').replace(/^models\//, '').trim();
  if (!strippedModel) return GeminiModel.PRO_3;
  if (strippedModel.startsWith('gemini-')) return strippedModel;
  return GeminiModel.PRO_3;
};

const extractJsonCandidate = (rawText: string): string => {
  const text = String(rawText || '').trim();
  if (!text) return text;

  const fenceMatch = text.match(/```(?:json)?\s*([\s\S]*?)\s*```/i);
  if (fenceMatch?.[1]) {
    return fenceMatch[1].trim();
  }

  const startBrace = text.indexOf('{');
  const startBracket = text.indexOf('[');
  const start = (startBrace !== -1 && (startBracket === -1 || startBrace < startBracket)) ? startBrace : startBracket;

  const endBrace = text.lastIndexOf('}');
  const endBracket = text.lastIndexOf(']');
  const end = (endBrace !== -1 && (endBracket === -1 || endBrace > endBracket)) ? endBrace : endBracket;

  if (start !== -1 && end !== -1 && end > start) {
    return text.slice(start, end + 1).trim();
  }

  return text;
};

const sanitizeJsonCandidate = (candidate: string): string => candidate
  .replace(/^\uFEFF/, '')
  .replace(/[“”]/g, '"')
  .replace(/[‘’]/g, "'")
  .replace(/,\s*([}\]])/g, '$1')
  .replace(/([{,]\s*)'([^']+?)'\s*:/g, '$1"$2":')
  .replace(/:\s*'([^']*?)'(\s*[,}\]])/g, ': "$1"$2');

const parseModelJson = <T>(rawText: string, fallback: T): T => {
  const baseCandidate = extractJsonCandidate(rawText);
  const candidates = [
    String(rawText || '').trim(),
    baseCandidate,
    sanitizeJsonCandidate(baseCandidate),
    sanitizeJsonCandidate(String(rawText || '').trim()),
  ].filter(Boolean);

  for (const candidate of candidates) {
    try {
      return JSON.parse(candidate) as T;
    } catch (error) {
      // Try the next recovery strategy.
    }
  }

  return fallback;
};

const inferLeadExpectation = (companyName: string, industry: string, reason: string, projectDescription: string = '') => {
  const signalText = `${companyName} ${industry} ${reason} ${projectDescription}`.toLowerCase();

  if (/(booking|boekings|entertainment|events|festival|bruiloft|artiest|optreden|muziek|muzikaal)/.test(signalText)) {
    return 'Verwacht meestal een professionele aanmelding met sterk repertoire, duidelijke positionering en direct bewijs van kwaliteit.';
  }

  if (/(software|saas|data|automation|crm|platform|agency|consult|marketing|sales)/.test(signalText)) {
    return 'Verwacht meestal een scherpe zakelijke propositie, relevante use-case en een korte introductie met aantoonbare waarde.';
  }

  if (/(horeca|hotel|restaurant|venue|locatie|hospitality)/.test(signalText)) {
    return 'Verwacht meestal een concreet voorstel dat direct aansluit op gastenbeleving, bezetting of omzet.';
  }

  return 'Verwacht meestal een korte, relevante benadering met duidelijke fit en een logische vervolgstap.';
};

const inferLeadLogic = (companyName: string, industry: string, location: string, website: string, reason: string) => {
  const fragments = [
    companyName ? `${companyName} opereert` : 'Dit bedrijf opereert',
    industry && industry !== 'Lead' ? `binnen ${industry}` : '',
    location ? `rond ${location}` : '',
    website ? `met een zichtbare online ingang via ${website}` : '',
  ].filter(Boolean);

  const baseLogic = fragments.join(' ').replace(/\s+/g, ' ').trim();
  const reasonText = reason?.trim();

  if (reasonText && !/^(relevant bedrijf voor dit project|relevant bedrijf uit de interne dataset)\.?$/i.test(reasonText)) {
    return `${baseLogic}. Relevantie: ${reasonText}`.replace(/\s+/g, ' ').trim();
  }

  return `${baseLogic}. Dit lijkt een logische prospect om inhoudelijk te kwalificeren voordat je outreach start.`.replace(/\s+/g, ' ').trim();
};

export const inferCompanyNameFromLeadText = (...values: any[]): string => {
  const text = values
    .filter((value) => typeof value === 'string' && value.trim())
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!text) return '';

  const explicitPatterns = [
    /\b(?:bedrijf|company|bedrijfsnaam|organisatie|org)\s*[:=-]\s*["“]?([A-Z0-9][A-Za-z0-9&.'’ -]{1,80})/i,
    /^["“]?([A-Z0-9][A-Za-z0-9&.'’ -]{1,80}?)["”]?\s+(?:is|biedt|organiseert|werkt|levert|maakt|verzorgt|specialiseert|heeft)\b/,
    /\b([A-Z][A-Z0-9&.'’ -]{2,50})\s+is\s+(?:de|een|het)\b/,
  ];

  for (const pattern of explicitPatterns) {
    const match = text.match(pattern);
    const candidate = match?.[1]?.replace(/[.,;:()[\]{}]+$/g, '').trim();
    if (candidate && !/^(onbekend bedrijf|bedrijf|company|deze lead|dit bedrijf)$/i.test(candidate)) {
      return candidate;
    }
  }

  const domainMatch = text.match(/\b(?:https?:\/\/)?(?:www\.)?([a-z0-9-]{3,})\.(?:nl|com|eu|org|be|de)\b/i);
  if (domainMatch?.[1]) {
    return domainMatch[1]
      .split('-')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  return '';
};

export const normalizeLeadItem = (item: any) => {
  const normalized = asObject(item);
  const contactInfo = asObject(
    normalized.contactInfo ||
    normalized.contactgegevens ||
    normalized.contact ||
    normalized.contacts
  );
  const companyName = firstString(
    normalized.companyName,
    normalized.Bedrijfsnaam,
    normalized.bedrijfsnaam,
    normalized.company,
    normalized.bedrijf,
    normalized.naam,
    normalized.name,
  ) || inferCompanyNameFromLeadText(
    normalized.description,
    normalized.beschrijving,
    normalized.reason,
    normalized.reden,
    normalized.summary,
    normalized.samenvatting,
    normalized.title,
    normalized.titel,
    normalized.website,
    normalized.url,
  );
  const industry = firstString(
    normalized.industry,
    normalized.branche,
    normalized.sector,
    normalized.categorie,
  ) || 'Lead';
  const website = firstString(
    contactInfo.website,
    contactInfo.url,
    normalized.website,
    normalized.Website,
    normalized.url,
    normalized.URL,
  );
  const email = firstString(
    contactInfo.email,
    contactInfo.e_mail,
    contactInfo.mail,
    normalized.email,
    normalized.e_mail,
    normalized.mail,
    normalized['e-mail'],
    normalized.Email,
  );
  const phone = firstString(
    contactInfo.phone,
    contactInfo.telefoon,
    contactInfo.tel,
    normalized.phone,
    normalized.telefoon,
    normalized.tel,
    normalized.Telefoon,
  );
  const location = firstString(
    contactInfo.address,
    contactInfo.adres,
    contactInfo.location,
    contactInfo.vestigingsadres,
    normalized.location,
    normalized.locatie,
    normalized['Stad/Locatie'],
    normalized.stad,
    normalized.plaats,
    normalized.address,
    normalized.adres,
  );
  const fallbackReasonParts = [
    companyName ? `${companyName} lijkt relevant voor dit project` : '',
    industry && industry !== 'Lead' ? `in de sector ${industry}` : '',
    location ? `rond ${location}` : '',
  ].filter(Boolean);
  const fallbackReason = fallbackReasonParts.length > 0
    ? `${fallbackReasonParts.join(' ')}.`
    : 'Relevant bedrijf voor dit project.';
  const fallbackNextStep = website
    ? `Start met onderzoek via ${website}.`
    : `Start met verdiepend onderzoek naar ${companyName || 'dit bedrijf'}.`;
  const resolvedReason = firstString(
    normalized.reason,
    normalized.reden,
    normalized.summary,
    normalized.samenvatting,
    normalized.waarom_relevant,
  ) || fallbackReason;

  return {
    companyName: companyName || 'Onbekend Bedrijf',
    reason: resolvedReason,
    suggestedNextStep: firstString(
      normalized.suggestedNextStep,
      normalized.volgendeStap,
      normalized['Volgende Stap'],
      normalized.nextStep,
      normalized.contactmethode,
    ) || fallbackNextStep,
    qualificationLogic: firstString(
      normalized.qualificationLogic,
      normalized.leadLogic,
      normalized.relevantieLogica,
    ) || inferLeadLogic(companyName || 'Dit bedrijf', industry, location, website, resolvedReason),
    buyerExpectation: firstString(
      normalized.buyerExpectation,
      normalized.expectedApproach,
      normalized.verwachting,
    ) || inferLeadExpectation(companyName || 'Dit bedrijf', industry, resolvedReason),
    industry,
    website,
    email,
    phone,
    location,
    contactInfo: {
      ...contactInfo,
      website: website || contactInfo.website || '',
      email: email || contactInfo.email || '',
      phone: phone || contactInfo.phone || '',
      address: location || contactInfo.address || '',
    },
    sources: Array.isArray(normalized.sources) ? normalized.sources : Array.isArray(normalized.bronnen) ? normalized.bronnen : [],
  };
};

export const isAcceptedLead = (lead: any): boolean => {
  const name = String(lead?.companyName || '').trim();
  if (!name || /^onbekend bedrijf$/i.test(name)) return false;

  const hasUsefulContext = Boolean(
    String(lead?.website || '').trim() ||
    String(lead?.reason || '').trim() ||
    String(lead?.qualificationLogic || '').trim()
  );

  return hasUsefulContext;
};

const hasRequiredLeadContact = (lead: any): boolean =>
  Boolean(
    String(lead?.email || lead?.contactInfo?.email || '').trim() ||
    String(lead?.phone || lead?.contactInfo?.phone || '').trim()
  );

const hasLocationOrWebsite = (lead: any): boolean =>
  Boolean(
    String(lead?.website || lead?.contactInfo?.website || '').trim() ||
    String(lead?.location || lead?.contactInfo?.address || '').trim()
  );

const enrichLeadContact = async (lead: any, project: Project) => {
  const settings = normalizeSettings(project.settings || getSettings());
  const region = getSearchRegionInstruction(settings);
  const enrichment = await ContactEnrichmentService.resolve({
    companyName: lead.companyName,
    queryContext: `${region.queryContext} ${project.name} ${project.description} ${lead.industry || ''}`.trim(),
    website: lead.website || lead.contactInfo?.website || '',
    location: lead.location || lead.contactInfo?.address || '',
  });

  if (!enrichment) return lead;

  const next = {
    ...lead,
    website: lead.website || enrichment.website || '',
    location: lead.location || enrichment.address || '',
    email: lead.email || enrichment.email || '',
    phone: lead.phone || enrichment.phone || '',
    contactInfo: {
      ...(lead.contactInfo || {}),
      website: lead.contactInfo?.website || enrichment.website || '',
      address: lead.contactInfo?.address || enrichment.address || '',
      email: lead.contactInfo?.email || enrichment.email || '',
      phone: lead.contactInfo?.phone || enrichment.phone || '',
    },
    enrichmentSources: enrichment.sources || [],
    source: lead.source || 'contact-enriched',
  };

  next.suggestedNextStep = next.email
    ? `Benader ${next.companyName} via ${next.email} en valideer de juiste beslisser.`
    : next.phone
      ? `Bel ${next.companyName} via ${next.phone} en valideer de juiste beslisser.`
      : next.suggestedNextStep;

  return next;
};

const discoverContactableLeads = async (project: Project, maxResults: number): Promise<any[]> => {
  const settings = normalizeSettings(project.settings || getSettings());
  const region = getSearchRegionInstruction(settings);
  const discovered = await ContactEnrichmentService.discover({
    query: `${project.name} ${project.description}`.trim(),
    queryContext: region.queryContext,
    maxResults,
  });

  return discovered
    .map((lead) => ({
      ...normalizeLeadItem({
        ...lead,
        location: lead.address,
      }),
      companyId: `web-${crypto.randomUUID()}`,
      source: 'web-discovery',
      enrichmentSources: lead.sources || [],
    }))
    .filter((lead) => isAcceptedLead(lead) && hasRequiredLeadContact(lead) && hasLocationOrWebsite(lead));
};

const parseLeadFinderPayload = (rawText: string): any[] => {
  const parsed = parseModelJson<any>(rawText, []);

  if (Array.isArray(parsed)) {
    return parsed;
  }

  const payload = asObject(parsed);
  const nestedArray = [
    payload.bedrijven,
    payload.leads,
    payload.results,
    payload.data,
    payload.items,
  ].find(Array.isArray);

  return Array.isArray(nestedArray) ? nestedArray : [];
};

const normalizeResearchData = (rawData: any, fallbackCompanyName: string, companyId?: string) => {
  const raw = unwrapObjectPayload(rawData);
  const analysis = asObject(
    raw.microsoft_nederland_analyse ||
    raw.bedrijfsanalyse ||
    raw.company_analysis ||
    raw.companyAnalysis ||
    raw.onderzoeksresultaat ||
    raw.researchResult ||
    raw.analysis ||
    raw.analyse
  );
  const general = asObject(raw.algemene_informatie || analysis.algemene_informatie || analysis.general);
  const contactSource = asObject(raw.contactInfo || raw.contactgegevens || analysis.contactgegevens || analysis.contactInfo);
  const verification = asObject(raw.gemini_capaciteit_verificatie || raw.verification);
  const strategicHooks = Array.isArray(raw.deepInsights)
    ? raw.deepInsights
    : (
      analysis.strategische_aanknopingspunten ||
      raw.strategische_aanknopingspunten ||
      raw.keyInsights ||
      raw.insights ||
      raw.belangrijkste_inzichten ||
      raw.salesAngles ||
      []
    );
  const commercialSignals = asObject(raw.commercialSignals || raw.commercial || raw.salesIntel || raw.sales_intel);
  const sources = mergeSourceLists(raw.sources, raw.bronnen, raw.references);
  const idealBuyer = firstString(
    raw.idealBuyer,
    raw.idealeKoper,
    raw.ideale_klant,
    raw.targetBuyer,
    commercialSignals.idealBuyer,
    commercialSignals.targetBuyer,
  );
  const salesHook = firstString(
    raw.salesHook,
    raw.salesAngle,
    raw.verkoophaak,
    raw.haak,
    commercialSignals.salesHook,
    commercialSignals.salesAngle,
  );
  const recommendedChannel = firstString(
    raw.recommendedChannel,
    raw.aanbevolenKanaal,
    raw.bestChannel,
    commercialSignals.recommendedChannel,
  );
  const doNotSay = firstString(
    raw.doNotSay,
    raw.vermijd,
    raw.risicoboodschap,
    commercialSignals.doNotSay,
  );
  const confidence = firstString(
    raw.confidence,
    raw.vertrouwen,
    raw.betrouwbaarheid,
    typeof raw.confidenceScore === 'number' ? `${Math.round(raw.confidenceScore * 100)}%` : '',
  );
  const painSignals = uniqueStringList(
    raw.painSignals,
    raw.pijnsignalen,
    raw.commercialPainSignals,
    commercialSignals.painSignals,
  );
  const timingSignals = uniqueStringList(
    raw.timingSignals,
    raw.tijdsignalen,
    raw.buyingSignals,
    raw.recentSignals,
    commercialSignals.timingSignals,
  );
  const missingData = uniqueStringList(
    raw.missingData,
    raw.ontbrekendeData,
    raw.dataGaps,
  );
  const evidence = normalizeEvidenceItems(raw.evidence || raw.bewijspunten || raw.proofPoints || raw.claims);

  const companyName = firstString(
    raw.companyName,
    raw.bedrijfsnaam,
    general.bedrijfsnaam,
    general.companyName,
    analysis.companyName,
    fallbackCompanyName,
  ) || 'Onbekend Bedrijf';

  let summary = firstString(
    raw.summary,
    raw.executiveSummary,
    raw.executive_summary,
    raw.overview,
    raw.companySummary,
    raw.company_summary,
    raw.bedrijfssamenvatting,
    raw.samenvatting,
    verification.toelichting,
    general.samenvatting,
    analysis.samenvatting,
    analysis.summary,
  ) || `${companyName} onderzocht.`;

  const deepInsights = uniqueStringList(
    strategicHooks,
    salesHook ? `Saleshaak: ${salesHook}` : '',
    idealBuyer ? `Ideale koper: ${idealBuyer}` : '',
    recommendedChannel ? `Aanbevolen kanaal: ${recommendedChannel}` : '',
    painSignals.map((signal) => `Pijnsignaal: ${signal}`),
    timingSignals.map((signal) => `Timing signaal: ${signal}`),
    evidence.slice(0, 3).map((item) =>
      item.source ? `${item.claim} (${item.source})` : item.claim
    ),
  );
  if ((summary === `${companyName} onderzocht.` || summary === 'Parse fout.') && deepInsights.length > 1) {
    summary = deepInsights.slice(0, 2).join(' ');
  }
  const normalized = {
    companyName,
    companyId,
    industry: firstString(
      raw.industry,
      general.primaire_activiteit,
      general.industry,
      analysis.industry,
    ) || 'Onbekend',
    summary,
    contactInfo: {
      website: firstString(
        contactSource.website,
        contactSource.url,
        contactSource.online_ondersteuning,
        contactSource.onlineSupport,
        raw.canonicalWebsite,
        raw.canonical_website,
        raw.officialWebsite,
        raw.website,
        raw.url,
        general.website,
        analysis.website,
        analysis.canonicalWebsite,
      ),
      email: firstString(
        contactSource.email,
        contactSource.e_mail,
        contactSource.mail,
        raw.email,
      ),
      phone: firstString(
        contactSource.phone,
        contactSource.telefoon,
        contactSource.algemeen_telefoonnummer,
        contactSource.klantenservice_telefoonnummer_consumenten,
        raw.phone,
      ),
      address: firstString(
        contactSource.address,
        contactSource.adres,
        contactSource.location,
        contactSource.vestigingsadres,
        general.hoofdkantoor_adres,
        general.address,
        general.adres,
        raw.address,
        raw.adres,
        raw.location,
        analysis.address,
        analysis.adres,
      ),
      keyContact: firstString(
        contactSource.keyContact,
        raw.keyContact,
        Array.isArray(analysis.strategische_aanknopingspunten) ? analysis.strategische_aanknopingspunten[0]?.relevante_personen : '',
      ),
    },
    deepInsights: deepInsights.length > 0 ? deepInsights : [summary],
    competitorAnalysis: uniqueStringList(
      raw.competitorAnalysis,
      raw.concurrentieAnalyse,
      raw.marketSignals,
      raw.competitorSignals,
    ),
    idealBuyer,
    painSignals,
    timingSignals,
    salesHook,
    recommendedChannel,
    doNotSay,
    confidence,
    missingData,
    evidence,
    sources,
  };

  return normalized;
};

const normalizeCompetitorData = (rawData: any, fallbackCompanyName: string, companyId?: string) => {
  const raw = unwrapObjectPayload(rawData);
  const topCompetitors = Array.isArray(raw.topCompetitors)
    ? raw.topCompetitors
    : Array.isArray(raw.competitors)
      ? raw.competitors.map((item: any) => ({
          name: firstString(item.name, item.companyName, item.bedrijfsnaam),
          strength: firstString(item.strength, item.sterkte, item.positioning, item.whyRelevant),
          weakness: firstString(item.weakness, item.zwakte, item.risk, item.limitation),
        }))
    : Array.isArray(raw.concurrenten)
      ? raw.concurrenten.map((item: any) => ({
          name: firstString(item.name, item.naam),
          strength: firstString(item.strength, item.sterkte, item.advantage),
          weakness: firstString(item.weakness, item.zwakte),
        }))
      : Array.isArray(raw.belangrijkste_concurrenten)
        ? raw.belangrijkste_concurrenten.map((item: any) => ({
            name: firstString(item.name, item.naam, item.bedrijfsnaam),
            strength: firstString(item.strength, item.sterkte, item.voordeel),
            weakness: firstString(item.weakness, item.zwakte, item.nadeel),
          }))
      : [];

  return {
    companyName: firstString(raw.companyName, raw.bedrijfsnaam, fallbackCompanyName) || 'Onbekend Bedrijf',
    companyId,
    topCompetitors,
    marketPosition: firstString(
      raw.marketPosition,
      raw.marktpositie,
      raw.marketShare,
      raw.positioning,
      raw.position,
      raw.samenvatting,
    ) || 'Onbekend',
    differentiationStrategy: firstString(
      raw.differentiationStrategy,
      raw.differentiatiestrategie,
      raw.competitiveAdvantage,
      raw.competitiveEdge,
      raw.summary,
      raw.samenvatting,
    ),
    sources: Array.isArray(raw.sources) ? raw.sources : [],
  };
};

const normalizeExtraResearchData = (rawData: any, fallbackCompanyName: string, companyId?: string) => {
  const raw = unwrapObjectPayload(rawData);
  return {
    companyName: firstString(raw.companyName, raw.bedrijfsnaam, fallbackCompanyName) || 'Onbekend Bedrijf',
    companyId,
    reviews: asStringArray(raw.reviews || raw.beoordelingen),
    sentiment: firstString(raw.sentiment, raw.toon, raw.samenvatting) || 'Onbekend',
    marketReputation: firstString(raw.marketReputation, raw.marktreputatie, raw.summary) || 'Geen reputatiedata gevonden.',
    onlinePresence: firstString(raw.onlinePresence, raw.onlineAanwezigheid),
    sources: Array.isArray(raw.sources) ? raw.sources : [],
  };
};

const generateTextAcrossProviders = async (
  settings: AppSettings, 
  prompt: string, 
  systemPrompt: string, 
  useSearch: boolean = false,
  responseSchema?: any,
  model: string = settings.defaultModel,
  thinkingBudget: number = settings.thinkingBudget
): Promise<{ text: string; groundingChunks?: any[] }> => {
  const provider = settings.provider;
  
  console.log(`[AgentRegistry] Routing to ${provider} (${model})`);

  try {
    if (provider === AIProvider.GEMINI) {
      const gemini = new GeminiService(settings.geminiKey);
      return await gemini.generateText(prompt, systemPrompt, model, useSearch, responseSchema, thinkingBudget);
    } else if (provider === AIProvider.OPENROUTER) {
      if (settings.costMode === CostMode.FREE_FIRST) {
        const result = await OpenRouterService.generateTextFreeFirst(settings.openRouterKey, prompt, systemPrompt, model);
        return { text: result.text };
      }

      if (settings.costMode === CostMode.CHEAP_PAID_FALLBACK) {
        try {
          const result = await OpenRouterService.generateTextFreeFirst(settings.openRouterKey, prompt, systemPrompt, model);
          return { text: result.text };
        } catch (error) {
          console.warn('[AgentRegistry] Free OpenRouter models failed, using paid auto fallback because Cheap Paid Fallback is enabled.', error);
        }
      }

      const selectedModel = settings.costMode === CostMode.BEST_QUALITY
        ? (model || OpenRouterService.PAID_AUTO_MODEL_ID)
        : OpenRouterService.PAID_AUTO_MODEL_ID;
      const text = await OpenRouterService.generateText(settings.openRouterKey, prompt, systemPrompt, selectedModel);
      return { text };
    } else if (provider === AIProvider.OLLAMA || provider === AIProvider.LMSTUDIO || provider === AIProvider.LOCAL) {
      const text = await LocalLLMService.generateText(settings.localUrl, prompt, systemPrompt, model, settings.localApiKey);
      return { text };
    }
  } catch (err) {
    console.error(`[AgentRegistry] Error with provider ${provider}:`, err);
    throw err;
  }
  
  return { text: "" };
};

const runDeepGeminiResearchPipeline = async (
  project: Project,
  options: { companyName: string; companyId?: string },
  settings: AppSettings,
  context: string
): Promise<{ data: any; sources: Array<{ title: string; uri: string }> }> => {
  const companyName = options?.companyName || 'Onbekend Bedrijf';
  const gemini = new GeminiService(settings.geminiKey);
  const model = selectGeminiResearchModel(settings.researchModel);
  const thinkingBudget = Math.max(settings.thinkingBudget || 0, 1024);

  const passOneSchema = {
    type: Type.OBJECT,
    properties: {
      companyName: { type: Type.STRING },
      canonicalWebsite: { type: Type.STRING },
      industry: { type: Type.STRING },
      location: { type: Type.STRING },
      businessModel: { type: Type.STRING },
      valueProposition: { type: Type.STRING },
      idealBuyer: { type: Type.STRING },
      summary: { type: Type.STRING },
      recentSignals: { type: Type.ARRAY, items: { type: Type.STRING } },
      missingData: { type: Type.ARRAY, items: { type: Type.STRING } }
    },
    required: ["companyName", "summary", "recentSignals", "missingData"]
  };

  const passOne = await gemini.generateText(
    `VOER EEN ENTITY RESOLUTION EN BEDRIJFSSCAN UIT VOOR "${companyName}".
${context}
Zoek het juiste bedrijf, de canonieke website, kernactiviteit, doelgroep, waardepropositie en recente marktsignalen.
Werk conservatief: als je iets niet publiek kunt bevestigen, zet het in "missingData".
Geef uitsluitend valide JSON terug.`,
    "Je bent een senior business intelligence researcher die eerst de juiste entiteit vaststelt voordat verdere analyse begint.",
    model,
    true,
    passOneSchema,
    thinkingBudget
  );

  const passOneData = parseModelJson<any>(passOne.text, {});

  const passTwoSchema = {
    type: Type.OBJECT,
    properties: {
      companyName: { type: Type.STRING },
      contactInfo: {
        type: Type.OBJECT,
        properties: {
          website: { type: Type.STRING },
          email: { type: Type.STRING },
          phone: { type: Type.STRING },
          address: { type: Type.STRING },
          keyContact: { type: Type.STRING }
        }
      },
      painSignals: { type: Type.ARRAY, items: { type: Type.STRING } },
      timingSignals: { type: Type.ARRAY, items: { type: Type.STRING } },
      competitorAnalysis: { type: Type.ARRAY, items: { type: Type.STRING } },
      recommendedChannel: { type: Type.STRING },
      evidence: {
        type: Type.ARRAY,
        items: {
          type: Type.OBJECT,
          properties: {
            claim: { type: Type.STRING },
            source: { type: Type.STRING },
            confidence: { type: Type.STRING }
          },
          required: ["claim"]
        }
      },
      missingData: { type: Type.ARRAY, items: { type: Type.STRING } }
    },
    required: ["companyName", "contactInfo", "painSignals", "timingSignals", "evidence", "missingData"]
  };

  const passTwo = await gemini.generateText(
    `DOE VERDIEPEND COMMERCIEEL ONDERZOEK VOOR "${companyName}" OP BASIS VAN DEZE BASISSCAN:
${JSON.stringify(passOneData)}
${context}
Zoek nu specifiek naar contactgegevens, kooptriggers, pijnsignalen, timing-signalen, concurrentiedruk en bewijs uit publieke bronnen.
Vul alleen gegevens in die aantoonbaar of redelijk sterk afleidbaar zijn. Ontbrekende velden moeten expliciet in "missingData" terugkomen.
Geef uitsluitend valide JSON terug.`,
    "Je bent een go-to-market intelligence specialist die commerciële signalen en bewijs prioriteert.",
    model,
    true,
    passTwoSchema,
    thinkingBudget
  );

  const passTwoData = parseModelJson<any>(passTwo.text, {});

  const passThreeSchema = {
    type: Type.OBJECT,
    properties: {
      companyName: { type: Type.STRING },
      industry: { type: Type.STRING },
      summary: { type: Type.STRING },
      contactInfo: {
        type: Type.OBJECT,
        properties: {
          website: { type: Type.STRING },
          email: { type: Type.STRING },
          phone: { type: Type.STRING },
          address: { type: Type.STRING },
          keyContact: { type: Type.STRING }
        }
      },
      deepInsights: { type: Type.ARRAY, items: { type: Type.STRING } },
      competitorAnalysis: { type: Type.ARRAY, items: { type: Type.STRING } },
      idealBuyer: { type: Type.STRING },
      painSignals: { type: Type.ARRAY, items: { type: Type.STRING } },
      timingSignals: { type: Type.ARRAY, items: { type: Type.STRING } },
      salesHook: { type: Type.STRING },
      recommendedChannel: { type: Type.STRING },
      doNotSay: { type: Type.STRING },
      confidence: { type: Type.STRING },
      missingData: { type: Type.ARRAY, items: { type: Type.STRING } },
      evidence: {
        type: Type.ARRAY,
        items: {
          type: Type.OBJECT,
          properties: {
            claim: { type: Type.STRING },
            source: { type: Type.STRING },
            confidence: { type: Type.STRING }
          },
          required: ["claim"]
        }
      }
    },
    required: ["companyName", "summary", "contactInfo", "deepInsights", "painSignals", "timingSignals", "missingData"]
  };

  const passThree = await gemini.generateText(
    `SYNTHETISEER EEN SALES-KLAAR DIEP ONDERZOEK VOOR "${companyName}".
Gebruik exclusief deze twee tussenresultaten:
BASISSCAN: ${JSON.stringify(passOneData)}
COMMERCIELE SCAN: ${JSON.stringify(passTwoData)}

Doel:
- schrijf een scherpe summary van 4 tot 7 zinnen
- formuleer minimaal 6 concrete deepInsights
- benoem expliciet idealBuyer, painSignals, timingSignals, salesHook, recommendedChannel, doNotSay en confidence
- houd missingData expliciet als er nog gaten zijn
- neem alleen evidence op die uit publieke bronnen of sterke afleiding komt
Geef uitsluitend valide JSON terug.`,
    "Je bent een principal revenue intelligence strategist die meerdere onderzoekspassen samenvoegt tot een bruikbaar salesdossier.",
    model,
    false,
    passThreeSchema,
    Math.max(Math.floor(thinkingBudget / 2), 0)
  );

  const passThreeData = parseModelJson<any>(passThree.text, {});
  const sources = mergeSourceLists(
    normalizeGroundingSources(passOne.groundingChunks),
    normalizeGroundingSources(passTwo.groundingChunks),
    normalizeGroundingSources(passThree.groundingChunks),
  );

  const normalized = normalizeResearchData(
    {
      ...passOneData,
      ...passTwoData,
      ...passThreeData,
      contactInfo: {
        ...asObject(passOneData.contactInfo),
        ...asObject(passTwoData.contactInfo),
        ...asObject(passThreeData.contactInfo),
      },
      competitorAnalysis: uniqueStringList(
        passOneData.competitorAnalysis,
        passTwoData.competitorAnalysis,
        passThreeData.competitorAnalysis,
      ),
      painSignals: uniqueStringList(
        passTwoData.painSignals,
        passThreeData.painSignals,
      ),
      timingSignals: uniqueStringList(
        passOneData.recentSignals,
        passTwoData.timingSignals,
        passThreeData.timingSignals,
      ),
      missingData: uniqueStringList(
        passOneData.missingData,
        passTwoData.missingData,
        passThreeData.missingData,
      ),
      evidence: [
        ...normalizeEvidenceItems(passTwoData.evidence),
        ...normalizeEvidenceItems(passThreeData.evidence),
      ],
      sources,
    },
    companyName,
    options?.companyId
  );

  return { data: normalized, sources };
};

const runCheapOpenRouterResearchPipeline = async (
  project: Project,
  options: { companyName: string; companyId?: string },
  settings: AppSettings,
  context: string
): Promise<{ data: any; sources: Array<{ title: string; uri: string }> }> => {
  const companyName = options?.companyName || 'Onbekend Bedrijf';
  const systemPrompt = [
    'Je bent een zuinige Nederlandse B2B research analyst.',
    'Gebruik alleen de aangeleverde context en redelijke afleiding uit die context.',
    'Gebruik geen betaalde search of premium research tools.',
    'Scheid bron-bevestigde claims van model-afleiding via evidence.confidence.',
    'Geef uitsluitend valide JSON terug, zonder markdown.'
  ].join('\n');

  const passOne = await OpenRouterService.generateTextFreeFirst(
    settings.openRouterKey,
    `PASS 1 - ENTITY RESOLUTION
Bedrijf: ${companyName}
Project: ${project.name}
Projectdoel: ${project.description}
Context:
${context}

Geef JSON met: companyName, canonicalWebsite, industry, businessModel, valueProposition, idealBuyer, summary, missingData, evidence.
Gebruik confidence "bron bevestigd" voor claims die letterlijk uit context komen en "model-afleiding" voor redelijke inschattingen.`,
    systemPrompt,
    settings.researchModel
  );
  const passOneData = parseModelJson<any>(passOne.text, {});

  const passTwo = await OpenRouterService.generateTextFreeFirst(
    settings.openRouterKey,
    `PASS 2 - CONTACT EN POSITIONERING
Basisdata: ${JSON.stringify(passOneData)}
Projectcontext:
${context}

Vul JSON aan met: contactInfo, painSignals, timingSignals, competitorAnalysis, recommendedChannel, missingData, evidence.
Laat onbekende velden leeg of zet ze in missingData. Verzin geen e-mail, telefoon of contactpersoon.`,
    systemPrompt,
    settings.researchModel
  );
  const passTwoData = parseModelJson<any>(passTwo.text, {});

  const passThree = await OpenRouterService.generateTextFreeFirst(
    settings.openRouterKey,
    `PASS 3 - SALES SYNTHESIS
Entity: ${JSON.stringify(passOneData)}
Commerciele scan: ${JSON.stringify(passTwoData)}

Maak een sales-klaar JSON dossier met: companyName, industry, summary, contactInfo, deepInsights, competitorAnalysis, idealBuyer, painSignals, timingSignals, salesHook, recommendedChannel, doNotSay, confidence, missingData, evidence.
Eisen:
- summary: 4 tot 6 concrete Nederlandse zinnen
- deepInsights: minimaal 5 concrete punten
- confidence moet "Laag", "Gemiddeld" of "Hoog" zijn
- onderscheid in evidence tussen "bron bevestigd" en "model-afleiding"`,
    systemPrompt,
    settings.researchModel
  );
  const passThreeData = parseModelJson<any>(passThree.text, {});

  const normalized = normalizeResearchData(
    {
      ...passOneData,
      ...passTwoData,
      ...passThreeData,
      contactInfo: {
        ...asObject(passOneData.contactInfo),
        ...asObject(passTwoData.contactInfo),
        ...asObject(passThreeData.contactInfo),
      },
      competitorAnalysis: uniqueStringList(
        passTwoData.competitorAnalysis,
        passThreeData.competitorAnalysis,
      ),
      painSignals: uniqueStringList(
        passTwoData.painSignals,
        passThreeData.painSignals,
      ),
      timingSignals: uniqueStringList(
        passOneData.recentSignals,
        passTwoData.timingSignals,
        passThreeData.timingSignals,
      ),
      missingData: uniqueStringList(
        passOneData.missingData,
        passTwoData.missingData,
        passThreeData.missingData,
      ),
      evidence: [
        ...normalizeEvidenceItems(passOneData.evidence),
        ...normalizeEvidenceItems(passTwoData.evidence),
        ...normalizeEvidenceItems(passThreeData.evidence),
      ],
      sources: [],
    },
    companyName,
    options?.companyId
  );

  return { data: normalized, sources: [] };
};

const getKnowledgeBaseContext = (project: Project): string => {
  let ctx = "";
  // 1. Kennisbank
  if (project.attachments && project.attachments.length > 0) {
    const docsText = project.attachments
      .filter(a => a.textContent && a.textContent.length > 10)
      .map(a => `--- BESTAND: ${a.name} ---\n${a.textContent}\n`)
      .join("\n");
    ctx += docsText ? `\nGEGEVENS UIT DE KENNISBANK VAN DE OPDRACHTGEVER:\n${docsText}\n` : "";
  }

  // 2. Global Memory (Geleerde lessen)
  ctx += MemoryService.getGlobalContext();

  // 3. Project Hive Mind (Geleerde patronen van andere bedrijven in dit project)
  ctx += MemoryService.getProjectWisdom(project.id);

  return ctx;
};

export interface IAgentImpl {
  run(project: Project, options?: any): Promise<AgentResult>;
}

class LeadFinderAgent implements IAgentImpl {
  async run(project: Project, options?: { mode: 'database' | 'web', count?: number, excludeIds?: string[], query?: string }): Promise<AgentResult> {
    const settings = normalizeSettings(project.settings || getSettings());
    const count = options?.count || 5;
    const mode = options?.mode || 'database';
    const context = getKnowledgeBaseContext(project);
    const skillContext = getSelectedSkillsContext(project, options);

    if (mode === 'database') {
      const data = (project.customData || []).slice(0, count).map(c => {
        const reasonParts = [
          c.name ? `${c.name} sluit waarschijnlijk aan op dit project` : '',
          c.description ? `omdat ${c.description}` : '',
          c.industry && c.industry !== 'Lead' ? `binnen ${c.industry}` : '',
          c.location ? `in ${c.location}` : '',
        ].filter(Boolean);
        const resolvedReason = reasonParts.length > 0
          ? `${reasonParts.join(' ').replace(/\s+/g, ' ').trim()}.`
          : 'Relevant bedrijf uit de interne dataset.';

        return {
          companyId: c.id,
          companyName: c.name,
          reason: resolvedReason,
          suggestedNextStep: c.website
            ? `Start met onderzoek via ${c.website}.`
            : c.email
              ? `Controleer contactfit en benader ${c.name} via e-mail.`
              : `Start met verdiepend onderzoek naar ${c.name}.`,
          qualificationLogic: inferLeadLogic(c.name, c.industry || 'Lead', c.location || '', c.website || '', resolvedReason),
          buyerExpectation: inferLeadExpectation(c.name, c.industry || 'Lead', resolvedReason, project.description),
          source: 'database'
        };
      });

      return {
        id: crypto.randomUUID(),
        agentId: 'lead-finder',
        projectId: project.id,
        timestamp: new Date().toISOString(),
        summary: `Gevonden: ${data.length} leads in de database.`,
        data
      };
    }

    // ── Live websearch (Firecrawl): echte bedrijven i.p.v. modelgeheugen ──
    const searchQuery = String(options?.query || project.description || '').trim();

    // ── Query-rewrite: van gebruikerszin naar scherpe zoekopdracht ──
    // "ik ben muzikant en zoek werk" wordt bv. "boekingskantoren en bands die een
    // bassist inhuren voor betaalde optredens in Nederland". Faalt de rewrite, dan
    // zoeken we gewoon met de originele zin.
    const buildOptimizedQuery = async (raw: string): Promise<string> => {
      try {
        const { text } = await generateTextAcrossProviders(
          settings,
          `Zet deze zoekopdracht om naar één korte, effectieve zoekopdracht voor een webzoekmachine die bedrijven en organisaties vindt.
Zoekopdracht: "${raw}"
Projectcontext: "${project.description}"

Regels:
- Maximaal 12 woorden.
- Geen aanhalingstekens, geen uitleg, geen opsomming.
- Geen merknamen of multinationals, tenzij de zoekopdracht daar expliciet om vraagt.
- Voeg regio of land toe als dat uit de context blijkt.
- Richt je op de PARTIJ DIE KAN BETALEN of de partij die gezocht wordt, niet op de gebruiker zelf.

Antwoord met alleen de zoekopdracht.`,
          'Je herschrijft zoekopdrachten voor een webzoekmachine. Antwoord met alleen de zoekopdracht, zonder uitleg of opmaak.',
          false
        );
        const cleaned = String(text || '')
          .split('\n')
          .map((line) => line.replace(/^["'\`\s]+|["'\`\s]+$/g, '').trim())
          .filter(Boolean)[0] || '';
        if (cleaned.length >= 3 && cleaned.length <= 200 && !cleaned.includes('```')) {
          return cleaned;
        }
      } catch (rewriteError) {
        console.warn('[LeadFinder] Query-rewrite mislukt, originele zin gebruikt:', rewriteError);
      }
      return raw;
    };

    if (mode === 'web' && searchQuery.length >= 3) {
      const optimizedQuery = await buildOptimizedQuery(searchQuery);
      try {
        const discovered = await LeadSearchService.discover(optimizedQuery, Math.max(count * 2, 10));
        const excluded = new Set((options?.excludeIds || []).map((name) => name.toLowerCase()));

        const cleanCompanyName = (raw: string, website?: string) => {
          let name = String(raw || '').trim();
          // Paginatitels zoals "Bassist gezocht - Muzikantenbank.NET" -> "Muzikantenbank.NET"
          const parts = name.split(/\s+[-|–—]\s+/);
          if (parts.length > 1) name = parts[parts.length - 1].trim();
          name = name.replace(/\s*[|–—]\s*(facebook|linkedin|instagram)\.?$/i, '').trim();
          if (!name && website) {
            try { name = new URL(website).hostname.replace(/^www\./, ''); } catch { /* negeren */ }
          }
          return name.slice(0, 90);
        };

        const liveLeads = discovered
          .map((item) => normalizeLeadItem({
            companyName: cleanCompanyName(item.name, item.website),
            website: item.website,
            reason: item.description,
            description: item.description,
            industry: item.industry,
            location: item.location,
            email: item.emails?.[0],
            phone: item.phones?.[0],
            qualificationLogic: item.description,
            buyerExpectation: 'Wil waarschijnlijk snel weten wat het kost en of het werkt.',
            suggestedNextStep: item.website
              ? `Bezoek ${item.website} en gebruik de contactpagina.`
              : 'Controleer de contactgegevens en benader ze direct.',
            source: 'websearch',
          }))
          .filter(isAcceptedLead)
          .filter((lead: any) => !excluded.has(String(lead.companyName || '').toLowerCase()))
          .map((lead: any) => ({ ...lead, companyId: `web-${crypto.randomUUID()}`, source: 'websearch' }))
          .slice(0, count);

        if (liveLeads.length > 0) {
          return {
            id: crypto.randomUUID(),
            agentId: 'lead-finder',
            projectId: project.id,
            timestamp: new Date().toISOString(),
            summary: `Gevonden: ${liveLeads.length} leads via live websearch.`,
            data: liveLeads,
          };
        }
      } catch (liveError) {
        console.warn('[LeadFinder] Live websearch mislukt, terugval op modelkennis:', liveError);
      }
    }

    const schema = {
      type: Type.ARRAY,
      items: {
        type: Type.OBJECT,
        properties: {
          companyName: { type: Type.STRING },
          reason: { type: Type.STRING },
          suggestedNextStep: { type: Type.STRING },
          qualificationLogic: { type: Type.STRING },
          buyerExpectation: { type: Type.STRING },
          industry: { type: Type.STRING },
          website: { type: Type.STRING },
          location: { type: Type.STRING }
        },
        required: ["companyName", "reason", "suggestedNextStep"]
      }
    };

    const excludeList = options?.excludeIds && options.excludeIds.length > 0
      ? ` SLUIT DE VOLGENDE BEDRIJVEN STRIKT UIT: ${options.excludeIds.join(', ')}.`
      : '';
    const region = getSearchRegionInstruction(settings);

    const candidateCount = Math.max(count * 4, 12);
    const prompt = `🟢 CLIENT CONTEXT
Dit is de client die de software gebruikt om leads te benaderen. Dit is hun eigen bedrijf/product/propositie. Zij verkopen aan de prospect.
Zoekopdracht van de gebruiker (dit is leidend): "${searchQuery}"
Projectomschrijving: "${project.description}"
Kennisbank context: ${context}
Skills context: ${skillContext}

🔵 PROSPECT CONTEXT
Dit is de prospect/lead die gezocht of onderzocht wordt. Verwar de gegevens en de context van de client NOOIT met die van de prospect. De client pitcht aan de prospect; de prospect koopt van de client.
Op dit moment zoeken we nieuwe kandidaat-bedrijven (prospects) die voldoen aan het profiel van de client.

Bepaal eerst uit de zoekopdracht wie de PROSPECT is: welke sector, welke regio, welk type organisatie. Zoek daarna alleen bedrijven die daar echt bij passen.

HARD: noem geen multinationals, beursgenoteerde concerns of bekende merknamen (Shell, ING, ABN AMRO, Philips, Unilever, Heineken, etc.) tenzij de zoekopdracht daar expliciet om vraagt. Geen generieke "grote bedrijven" als je niet kunt uitleggen waarom juist zij bij deze zoekopdracht passen.

Vind ${candidateCount} kandidaat-bedrijven voor het doel van de client.${excludeList}
${region.prompt}
De live websearch is niet beschikbaar. Werk met beschikbare modelkennis, meegegeven projectcontext en eventuele kennisbanktekst. Verzin geen bedrijven die niet bestaan: bij twijfel liever minder leads.
Prioriteit is CONTACTEERBAARHEID: website, adres, telefoon en e-mail zijn belangrijker dan algemene relevantie.
Lever liever minder bedrijven met contactgegevens dan veel bedrijven zonder telefoon/e-mail.
Zoek of redeneer per bedrijf direct: website, stad/adres, telefoon en e-mail als die betrouwbaar beschikbaar zijn.
Belangrijk: zet nooit "Onbekend Bedrijf" in companyName. Als je alleen een beschrijving hebt zoals "MOJO is ...", extraheer dan "MOJO" als companyName.
Geef per lead:
- "reason": een korte inhoudelijke kwalificatie in 1 zin
- "website", "location", "email", "phone" waar betrouwbaar beschikbaar
- "qualificationLogic": wat dit bedrijf doet en waarom het logisch aansluit
- "buyerExpectation": wat deze lead waarschijnlijk verwacht van een eerste benadering of aanmelding
- "suggestedNextStep": de beste concrete vervolgstap
Geef ALLEEN een JSON array terug. Geen markdown, geen uitleg, geen code fences.`;

    const parseLeadData = (rawText: string) =>
      parseLeadFinderPayload(rawText)
        .map((item: any) => ({
          ...normalizeLeadItem(item),
          companyId: `web-${crypto.randomUUID()}`,
          source: 'openrouter-free'
        }))
        .filter(isAcceptedLead);

    const attemptLeadFinder = async (modelOverride?: string) => {
      const { text } = await generateTextAcrossProviders(
        settings,
        prompt,
        "Je bent een expert lead finder.",
        true,
        schema,
        modelOverride || settings.defaultModel
      );

      return { text, data: parseLeadData(text) };
    };

    let data: any[] = [];
    let rawLeadText = '';
    let usedFallbackModel = false;

    try {
      const primaryAttempt = await attemptLeadFinder();
      rawLeadText = primaryAttempt.text;
      data = primaryAttempt.data;
    } catch (error) {
      if (settings.provider !== AIProvider.OPENROUTER || settings.defaultModel === OpenRouterService.AUTO_MODEL_ID) {
        throw error;
      }

      console.warn(`[LeadFinder] Primary OpenRouter model failed, retrying with ${OpenRouterService.AUTO_MODEL_ID}.`, error);
      const fallbackAttempt = await attemptLeadFinder(OpenRouterService.AUTO_MODEL_ID);
      rawLeadText = fallbackAttempt.text;
      data = fallbackAttempt.data;
      usedFallbackModel = true;
    }

    if (data.length === 0 && settings.provider === AIProvider.OPENROUTER && settings.defaultModel !== OpenRouterService.AUTO_MODEL_ID) {
      console.warn(`[LeadFinder] Model ${settings.defaultModel} returned no parseable leads, retrying with ${OpenRouterService.AUTO_MODEL_ID}.`);
      const fallbackAttempt = await attemptLeadFinder(OpenRouterService.AUTO_MODEL_ID);
      rawLeadText = fallbackAttempt.text;
      data = fallbackAttempt.data;
      usedFallbackModel = true;
    }

    const candidates = data.slice(0, candidateCount);
    const enrichedResults = await Promise.all(
      candidates.map(async (lead) => {
        try {
          return await enrichLeadContact(lead, project);
        } catch (e) {
          console.error('[LeadFinder] Error enriching lead:', e);
          return null;
        }
      })
    );

    const enriched: any[] = [];
    const seenLeadNames = new Set<string>();
    for (const enrichedLead of enrichedResults) {
      if (!enrichedLead) continue;
      const leadKey = String(enrichedLead.companyName || '').trim().toLowerCase();
      if (hasRequiredLeadContact(enrichedLead) && hasLocationOrWebsite(enrichedLead) && !seenLeadNames.has(leadKey)) {
        enriched.push(enrichedLead);
        seenLeadNames.add(leadKey);
      }
      if (enriched.length >= count) break;
    }

    if (enriched.length < count) {
      const discovered = await discoverContactableLeads(project, Math.max(count - enriched.length, 3));
      for (const lead of discovered) {
        const leadKey = String(lead.companyName || '').trim().toLowerCase();
        if (seenLeadNames.has(leadKey)) continue;
        enriched.push(lead);
        seenLeadNames.add(leadKey);
        if (enriched.length >= count) break;
      }
    }
    data = enriched;

    if (!data.length) {
      console.error("LeadFinder found no contactable leads after enrichment", rawLeadText);
    }

    const result = {
      id: crypto.randomUUID(),
      agentId: 'lead-finder',
      projectId: project.id,
      timestamp: new Date().toISOString(),
      summary: usedFallbackModel
        ? `${data.length} contacteerbare leads gevonden na fallback naar ${OpenRouterService.AUTO_MODEL_NAME}.`
        : `${data.length} contacteerbare leads gevonden.`,
      data
    };

    // Auto-log initial dossiers for new leads
    data.forEach((lead: any) => {
      MemoryService.initMemory(lead.companyId, project.id, 'Lead Zoeker');
    });

    return result;
  }
}

class ResearchAgent implements IAgentImpl {
  async run(project: Project, options?: { companyName: string, companyId?: string, mode?: 'deep' | 'extra' | 'competitor' }): Promise<AgentResult> {
    const settings = normalizeSettings(project.settings || getSettings());
    const mode = options?.mode || 'deep';
    const context = getKnowledgeBaseContext(project);
    const skillContext = getSelectedSkillsContext(project, options);

    // --- CONCURRENTIE ANALYSE ---
    if (mode === 'competitor') {
      const schema = {
        type: Type.OBJECT,
        properties: {
          companyName: { type: Type.STRING },
          topCompetitors: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                name: { type: Type.STRING },
                strength: { type: Type.STRING },
                weakness: { type: Type.STRING }
              }
            }
          },
          marketPosition: { type: Type.STRING },
          differentiationStrategy: { type: Type.STRING }
        },
        required: ["companyName", "topCompetitors", "marketPosition", "differentiationStrategy"]
      };

      const prompt = `🟢 CLIENT CONTEXT
Dit is de client die de software gebruikt om leads te benaderen. Dit is hun eigen bedrijf/product/propositie. Zij verkopen aan de prospect.
Projectomschrijving: "${project.description}"
Kennisbank context: ${context}
Skills context: ${skillContext}

🔵 PROSPECT CONTEXT
Dit is de prospect/lead die gezocht of onderzocht wordt. Verwar de gegevens en de context van de client NOOIT met die van de prospect. De client pitcht aan de prospect; de prospect koopt van de client.
Te onderzoeken prospect/lead: "${options?.companyName}"

ANALYSEER DE CONCURRENTIEPOSITIE VAN DE PROSPECT "${options?.companyName}".
Gebruik web search en geef uitsluitend JSON terug.
Vereisten:
- "topCompetitors": minimaal 3 directe concurrenten van de prospect met per concurrent een naam, belangrijkste sterkte en duidelijke zwakte.
- "marketPosition": 4 tot 6 zinnen over positionering, doelgroep, onderscheid en commerciële bedreigingen van de prospect.
- "differentiationStrategy": 3 tot 5 zinnen met hoe de prospect zich kan onderscheiden in sales en propositie (of hoe de client zich ten opzichte van hen onderscheidt).
Gebruik geen lege array tenzij er echt geen publieke concurrenten vindbaar zijn; motiveer dat dan expliciet in "marketPosition".`;

      const { text, groundingChunks } = await generateTextAcrossProviders(
        settings,
        prompt,
        "Jij bent een Strategic Market Analyst.",
        true, // Use Search
        schema,
        settings.researchModel,
        settings.thinkingBudget
      );

      let data: any = {};
      const parsed = parseModelJson<any>(text, null);
      if (parsed) {
        data = normalizeCompetitorData(parsed, options?.companyName || '', options?.companyId);
      } else {
        console.error("Failed to parse competitor research JSON", text);
        data = { companyName: options?.companyName, companyId: options?.companyId, error: "Parse fout." };
      }
      data.sources = (groundingChunks ?? []).map((chunk: any) => chunk.web ? { title: chunk.web.title, uri: chunk.web.uri } : null).filter((s: any) => s !== null);

      const result = {
        id: crypto.randomUUID(),
        agentId: 'researcher-competitor', // Specifieke ID voor UI filtering
        projectId: project.id,
        timestamp: new Date().toISOString(),
        summary: `Concurrentie Analyse voor ${options?.companyName} voltooid.`,
        data
      };

      if (options?.companyId) {
        MemoryService.addInteraction(options.companyId, project.id, {
          id: crypto.randomUUID(),
          type: 'system_log',
          direction: 'inbound',
          timestamp: result.timestamp,
          summary: result.summary,
          sentiment: 'neutral',
          details: data
        });
      }

      return result;
    }

    // --- SENTIMENT & REPUTATIE (EXTRA) ---
    if (mode === 'extra') {
      const schema = {
        type: Type.OBJECT,
        properties: {
          companyName: { type: Type.STRING },
          reviews: { type: Type.ARRAY, items: { type: Type.STRING } },
          sentiment: { type: Type.STRING },
          marketReputation: { type: Type.STRING },
          onlinePresence: { type: Type.STRING }
        },
        required: ["companyName", "reviews", "sentiment", "marketReputation"]
      };

      const prompt = `🟢 CLIENT CONTEXT
Dit is de client die de software gebruikt om leads te benaderen. Dit is hun eigen bedrijf/product/propositie. Zij verkopen aan de prospect.
Projectomschrijving: "${project.description}"
Kennisbank context: ${context}
Skills context: ${skillContext}

🔵 PROSPECT CONTEXT
Dit is de prospect/lead die gezocht of onderzocht wordt. Verwar de gegevens en de context van de client NOOIT met die van de prospect. De client pitcht aan de prospect; de prospect koopt van de client.
Te onderzoeken prospect/lead: "${options?.companyName}"

VOER EEN SENTIMENT- EN REPUTATIE SCAN UIT VOOR DE PROSPECT "${options?.companyName}".
Zoek naar recente reviews, nieuwsberichten en algemene publieke opinie van de prospect.
Geef een gedetailleerde analyse in JSON-formaat.`;

      const { text, groundingChunks } = await generateTextAcrossProviders(
        settings,
        prompt,
        "Jij bent een Consumer Intelligence Specialist.",
        true,
        schema,
        settings.researchModel,
        settings.thinkingBudget
      );

      let data: any = {};
      const parsed = parseModelJson<any>(text, null);
      if (parsed) {
        data = normalizeExtraResearchData(parsed, options?.companyName || '', options?.companyId);
      } else {
        console.error("Failed to parse extra research JSON", text);
        data = { companyName: options?.companyName, companyId: options?.companyId, error: "Parse fout." };
      }
      data.sources = (groundingChunks ?? []).map((chunk: any) => chunk.web ? { title: chunk.web.title, uri: chunk.web.uri } : null).filter((s: any) => s !== null);

      const result = {
        id: crypto.randomUUID(),
        agentId: 'researcher-extra',
        projectId: project.id,
        timestamp: new Date().toISOString(),
        summary: `Sentiment & Reputatie Scan voor ${options?.companyName} voltooid.`,
        data
      };

      if (options?.companyId) {
        MemoryService.addInteraction(options.companyId, project.id, {
          id: crypto.randomUUID(),
          type: 'system_log',
          direction: 'inbound',
          timestamp: result.timestamp,
          summary: result.summary,
          sentiment: 'neutral',
          details: data
        });
      }

      return result;
    }

    // --- STANDAARD / DEEP RESEARCH ---
    const isDeep = options?.mode === 'deep';
    const modelToUse = isDeep ? settings.researchModel : settings.defaultModel;
    const budget = isDeep ? settings.thinkingBudget : 0;

    const schema = {
      type: Type.OBJECT,
      properties: {
        companyName: { type: Type.STRING },
        industry: { type: Type.STRING },
        summary: { type: Type.STRING },
        contactInfo: {
          type: Type.OBJECT,
          properties: {
            website: { type: Type.STRING },
            email: { type: Type.STRING },
            phone: { type: Type.STRING },
            address: { type: Type.STRING },
            keyContact: { type: Type.STRING }
          }
        },
        deepInsights: { type: Type.ARRAY, items: { type: Type.STRING } },
        competitorAnalysis: { type: Type.ARRAY, items: { type: Type.STRING } }
      },
      required: ["companyName", "summary", "contactInfo", "deepInsights"]
    };

    const searchInstruction = settings.provider === AIProvider.GEMINI
      ? 'Gebruik web search waar beschikbaar en lever uitsluitend JSON terug.'
      : 'Gebruik geen betaalde web-search tools; werk met beschikbare context en lever uitsluitend JSON terug.';
    const prompt = `🟢 CLIENT CONTEXT
Dit is de client die de software gebruikt om leads te benaderen. Dit is hun eigen bedrijf/product/propositie. Zij verkopen aan de prospect.
Projectomschrijving: "${project.description}"
Kennisbank context: ${context}
Skills context: ${skillContext}

🔵 PROSPECT CONTEXT
Dit is de prospect/lead die gezocht of onderzocht wordt. Verwar de gegevens en de context van de client NOOIT met die van de prospect. De client pitcht aan de prospect; de prospect koopt van de client.
Te onderzoeken prospect/lead: "${options?.companyName}"

Onderzoek de prospect "${options?.companyName}" in context van de client.
    ${searchInstruction}
    Vul het resultaat inhoudelijk en concreet in. Vermijd generieke zinnen zoals "${options?.companyName} onderzocht.".
    Vereisten:
    - "summary": 4 tot 7 zinnen met bedrijfsactiviteit, doelgroep, waardepropositie, recente signalen en verkoophaak.
    - "deepInsights": minimaal 5 concrete inzichten met sales- of strategie-waarde.
    - "contactInfo": vul website, e-mail, telefoon, adres en keyContact waar aantoonbaar te vinden.
    - "competitorAnalysis": noem 2 tot 4 relevante concurrentie- of marktinzichten.
    Als informatie ontbreekt, benoem specifiek wat niet publiek vindbaar was in plaats van een generieke placeholder te geven.`;

    let data: any = {};
    let groundingSources: Array<{ title: string; uri: string }> = [];

    if (isDeep && settings.provider === AIProvider.GEMINI && settings.geminiKey) {
      try {
        const deepPipelineResult = await runDeepGeminiResearchPipeline(
          project,
          { companyName: options?.companyName || '', companyId: options?.companyId },
          settings,
          `${context}${skillContext}`
        );
        data = deepPipelineResult.data;
        groundingSources = deepPipelineResult.sources;
      } catch (error) {
        console.error("Deep Gemini research pipeline failed, falling back to single-pass research.", error);
      }
    }

    if (
      isDeep &&
      settings.provider === AIProvider.OPENROUTER &&
      settings.openRouterKey &&
      settings.costMode !== CostMode.BEST_QUALITY
    ) {
      try {
        const cheapPipelineResult = await runCheapOpenRouterResearchPipeline(
          project,
          { companyName: options?.companyName || '', companyId: options?.companyId },
          settings,
          `${context}${skillContext}`
        );
        data = cheapPipelineResult.data;
        groundingSources = cheapPipelineResult.sources;
      } catch (error) {
        console.error("Cheap OpenRouter research pipeline failed, falling back to single-pass research.", error);
      }
    }

    if (!data || Object.keys(data).length === 0) {
      const res = await generateTextAcrossProviders(
        settings,
        prompt,
        isDeep ? "Jij bent een Elite Business Intelligence Agent." : "Je bent een business researcher.",
        true,
        schema,
        modelToUse,
        budget
      );

      const parsedResearch = parseModelJson<any>(res.text, null);
      if (parsedResearch) {
        data = normalizeResearchData(parsedResearch, options?.companyName || '', options?.companyId);
      } else {
        console.error("Failed to parse deep research JSON", res.text);
        data = {
          companyName: options?.companyName,
          companyId: options?.companyId,
          summary: "Parse fout.",
          contactInfo: {},
          deepInsights: [],
          competitorAnalysis: [],
        };
      }

      groundingSources = normalizeGroundingSources((res as any).groundingChunks);
    }

    if (groundingSources.length > 0) {
      data.sources = mergeSourceLists(data.sources, groundingSources);
    }

    if (data && !data.confidence && data.sources?.length > 0) {
      data.confidence = data.sources.length >= 4 ? 'Hoog' : 'Gemiddeld';
    }

    if (data && (!data.deepInsights || data.deepInsights.length < 5)) {
      data.deepInsights = uniqueStringList(
        data.deepInsights,
        data.painSignals?.map((item: string) => `Pijnsignaal: ${item}`),
        data.timingSignals?.map((item: string) => `Timing signaal: ${item}`),
        data.salesHook ? `Saleshaak: ${data.salesHook}` : '',
      );
    }

    const result = {
      id: crypto.randomUUID(),
      agentId: 'researcher',
      projectId: project.id,
      timestamp: new Date().toISOString(),
      summary: `${isDeep ? 'Diep Onderzoek' : 'Onderzoek'} voor ${options?.companyName} afgerond.`,
      data,
      isDeepResearch: isDeep
    };

    if (options?.companyId) {
      MemoryService.addInteraction(options.companyId, project.id, {
        id: crypto.randomUUID(),
        type: 'system_log',
        direction: 'inbound',
        timestamp: result.timestamp,
        summary: result.summary,
        sentiment: 'neutral',
        details: data
      });
    }

    return result;
  }
}

const buildFallbackStrategyData = (research: any) => {
  const companyName = firstString(research.companyName, research.name, 'Onbekend Bedrijf');
  const summary = firstString(research.summary, research.salesHook, research.valueProposition);
  const idealBuyer = firstString(research.idealBuyer, 'beslisser voor marketing, events of commerciële groei');
  const salesHook = firstString(
    research.salesHook,
    research.deepInsights?.[0],
    summary,
    `Koppel de propositie direct aan een concreet bedrijfsdoel van ${companyName}.`
  );
  const painSignals = asStringArray(research.painSignals);
  const timingSignals = asStringArray(research.timingSignals);
  const missingData = asStringArray(research.missingData);
  const contactInfo = asObject(research.contactInfo);

  const keyPitchPoints = uniqueStringList(
    salesHook,
    research.recommendedChannel ? `Benader via ${research.recommendedChannel} met een korte zakelijke aanleiding.` : '',
    painSignals[0] ? `Haak in op pijnsignaal: ${painSignals[0]}` : '',
    timingSignals[0] ? `Gebruik timing-signaal: ${timingSignals[0]}` : '',
    contactInfo.website ? `Verwijs naar zichtbare context op ${contactInfo.website}.` : '',
    missingData.length ? `Valideer eerst ontbrekende data: ${missingData.slice(0, 2).join(', ')}.` : '',
  ).slice(0, 5);

  return {
    companyName,
    companyId: research.companyId,
    strategyTitle: `Consultative trigger-strategie voor ${companyName}`,
    approachAngle: `Open met een korte, feitelijke aanleiding en toets eerst of ${idealBuyer} herkent dat dit relevant is. Gebruik daarna één concrete haak: ${salesHook}`,
    keyPitchPoints: keyPitchPoints.length > 0 ? keyPitchPoints : [
      `Start met een korte validatievraag over de actuele prioriteit van ${companyName}.`,
      'Noem één voordeel tegelijk en vraag daarna om herkenning.',
      'Stuur alleen bij interesse naar een concrete vervolgstap.',
    ],
    objectionHandling: uniqueStringList(
      'Geen tijd: vraag om de juiste persoon of een kort moment later.',
      'Geen interesse: erken kort en vraag of timing of relevantie het probleem is.',
      'Stuur maar info: vraag welke invalshoek relevant is voordat je iets mailt.',
    ),
  };
};

class StrategyAgent implements IAgentImpl {
  async run(project: Project, options?: { researchData: any }): Promise<AgentResult> {
    const settings = normalizeSettings(project.settings || getSettings());
    const context = getKnowledgeBaseContext(project);
    const skillContext = getSelectedSkillsContext(project, options);
    const normalizedResearchInput = normalizeResearchData(
      options?.researchData || {},
      firstString(options?.researchData?.companyName, options?.researchData?.bedrijfsnaam, 'Onbekend Bedrijf'),
      options?.researchData?.companyId,
    );
    const targetCompanyName = normalizedResearchInput.companyName;

    const schema = {
      type: Type.OBJECT,
      properties: {
        companyName: { type: Type.STRING },
        strategyTitle: { type: Type.STRING },
        approachAngle: { type: Type.STRING },
        keyPitchPoints: { type: Type.ARRAY, items: { type: Type.STRING } },
        objectionHandling: { type: Type.ARRAY, items: { type: Type.STRING } }
      },
      required: ["companyName", "strategyTitle", "approachAngle", "keyPitchPoints"]
    };

    const fallbackStrategy = buildFallbackStrategyData(normalizedResearchInput);
    const { text } = await generateTextAcrossProviders(
      settings,
      `🟢 CLIENT CONTEXT
Dit is de client die de software gebruikt om leads te benaderen. Dit is hun eigen bedrijf/product/propositie. Zij verkopen aan de prospect.
Projectomschrijving: "${project.description}"
Kennisbank context: ${context}
Skills context: ${skillContext}

🔵 PROSPECT CONTEXT
Dit is de prospect/lead die gezocht of onderzocht wordt. Verwar de gegevens en de context van de client NOOIT met die van de prospect. De client pitcht aan de prospect; de prospect koopt van de client.
Te analyseren prospect/lead: "${targetCompanyName}"
Onderzoeksdata van de prospect:
${JSON.stringify(normalizedResearchInput)}

Bepaal een concrete sales- en belstrategie voor de prospect ${targetCompanyName} namens de client.

Geef ALLEEN valide JSON terug met:
{
  "companyName": "...",
  "strategyTitle": "korte titel",
  "approachAngle": "concrete benaderingshoek in 2-4 zinnen",
  "keyPitchPoints": ["minimaal 3 concrete punten"],
  "objectionHandling": ["minimaal 3 bezwaarreacties"]
}

Regels:
- approachAngle mag nooit leeg, generiek of "Geen strategiehoek gegenereerd" zijn.
- Gebruik website/contactinfo/missingData als input voor een praktische volgorde.
- Als data ontbreekt, maak een validatiestrategie in plaats van te stoppen.`,
      "Je bent een senior revenue strategist die altijd een bruikbare, concrete strategie oplevert op basis van beschikbare en ontbrekende data.",
      false,
      schema
    );

    let strategyData: any = parseModelJson<any>(text, {});
    const rawStrategy = asObject(strategyData.strategy || strategyData.strategie || strategyData.plan);
    strategyData = { ...rawStrategy, ...strategyData };

    strategyData = {
      companyName: firstString(strategyData.companyName, targetCompanyName) || 'Onbekend Bedrijf',
      companyId: firstString(strategyData.companyId, normalizedResearchInput.companyId),
      strategyTitle: firstString(
        strategyData.strategyTitle,
        strategyData.title,
        strategyData.titel,
        fallbackStrategy.strategyTitle,
      ),
      approachAngle: firstString(
        strategyData.approachAngle,
        strategyData.angle,
        strategyData.hoek,
        strategyData.benaderingshoek,
        strategyData.summary,
        strategyData.samenvatting,
        fallbackStrategy.approachAngle,
      ),
      keyPitchPoints: uniqueStringList(
        strategyData.keyPitchPoints,
        strategyData.pitchPoints,
        strategyData.valueAnchors,
        strategyData.ankers,
        fallbackStrategy.keyPitchPoints,
      ).slice(0, 6),
      objectionHandling: uniqueStringList(
        strategyData.objectionHandling,
        strategyData.objections,
        strategyData.bezwaren,
        fallbackStrategy.objectionHandling,
      ).slice(0, 6),
    };

    const result = {
      id: crypto.randomUUID(),
      agentId: 'strategy-agent',
      projectId: project.id,
      timestamp: new Date().toISOString(),
      summary: `Strategie voor ${targetCompanyName} bepaald.`,
      data: strategyData
    };

    if (normalizedResearchInput.companyId) {
      MemoryService.addInteraction(normalizedResearchInput.companyId, project.id, {
        id: crypto.randomUUID(),
        type: 'system_log',
        direction: 'inbound',
        timestamp: result.timestamp,
        summary: result.summary,
        sentiment: 'neutral',
        details: result.data
      });
    }

    return result;
  }
}


// ... (existing imports)
import { EmailAgentService } from './emailAgent';

class EmailOutreachAgent implements IAgentImpl {
  async run(
      project: Project,
      options?: {
        companyId?: string;
        companyName?: string;
        companySnapshot?: Partial<Company>;
        tone?: 'professional' | 'casual';
        type?: 'initial' | 'followup';
      }
  ): Promise<AgentResult> {
    const normalizedCompanyName = options?.companyName?.trim().toLowerCase();
    const projectCompanies = project.customData || [];

    const company =
      projectCompanies.find((entry) => entry.id === options?.companyId) ||
      projectCompanies.find((entry) => entry.name?.trim().toLowerCase() === normalizedCompanyName) ||
      (options?.companySnapshot
        ? {
            id: options.companySnapshot.id || options.companyId || crypto.randomUUID(),
            name: options.companySnapshot.name || options.companyName || 'Onbekend bedrijf',
            description: options.companySnapshot.description || '',
            industry: options.companySnapshot.industry || 'Lead',
            website: options.companySnapshot.website || '',
            location: options.companySnapshot.location || '',
            email: options.companySnapshot.email || '',
            phone: options.companySnapshot.phone || '',
            contactName: options.companySnapshot.contactName,
          }
        : null);

    if (!company) throw new Error("Bedrijf niet gevonden");

    const draft = await EmailAgentService.draftEmail(
      project,
      company,
      options?.type || 'initial',
      options?.tone || 'professional'
    );

    const result = {
      id: crypto.randomUUID(),
      agentId: 'email-outreach',
      projectId: project.id,
      timestamp: new Date().toISOString(),
      summary: `E-mailconcept opgesteld voor ${company.name}.`,
      data: draft
    };

    return result;
  }
}

const registry: Record<string, IAgentImpl> = {
  'lead-finder': new LeadFinderAgent(),
  'researcher': new ResearchAgent(),
  'researcher-competitor': new ResearchAgent(), // Reuses ResearchAgent, relies on options.mode='competitor'
  'researcher-extra': new ResearchAgent(),      // Reuses ResearchAgent, relies on options.mode='extra'
  'strategy-agent': new StrategyAgent(),
  'email-outreach': new EmailOutreachAgent(),
};

export const AGENT_DEFINITIONS: Agent[] = [
  { id: 'lead-finder', name: 'Lead Zoeker', type: 'lead-finder', description: 'Vind leads via database of web.', icon: null },
  { id: 'researcher', name: 'Onderzoeker', type: 'researcher', description: 'Onderzoek leads op contactinfo en inzichten.', icon: null },
  { id: 'strategy-agent', name: 'Strateeg', type: 'strategy-agent', description: 'Bepaal de aanpak per bedrijf.', icon: null },
  { id: 'email-outreach', name: 'E-mail Agent', type: 'email-outreach', description: 'Stel gepersonaliseerde emails op.', icon: null }
];

export const runAgent = async (agentId: string, project: Project, options?: any) => {
  const agent = registry[agentId];
  if (!agent) throw new Error(`Agent ${agentId} niet gevonden`);
  return await agent.run(project, options);
};
